import * as React from 'react'
import { Building2, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { useT, useVisitOne } from '@/components/visitone/visitone-provider'

export type TenantOption = { id: string; name: string }

function fold(value: string, locale: string) {
  return value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLocaleLowerCase(locale)
}

/*
 * TenantPicker: one searchable list of tenant names, used by the sign-in
 * Choose page and the Switch tenant dialog. Combobox pattern with the
 * CommandPalette keyboard model: type to filter (accents ignored), Up/Down
 * move, Home/End jump, Enter chooses. Names only; the current tenant is
 * marked with text as well as an icon. The result count is announced politely.
 */
export function TenantPicker({
  tenants,
  currentId,
  onChoose,
  autoFocus = true,
}: {
  tenants: TenantOption[]
  currentId?: string
  onChoose: (tenant: TenantOption) => void
  autoFocus?: boolean
}) {
  const t = useT()
  const { locale } = useVisitOne()
  const [query, setQuery] = React.useState('')
  const [active, setActive] = React.useState(0)
  const listId = React.useId()
  const inputRef = React.useRef<HTMLInputElement>(null)

  const sorted = React.useMemo(() => {
    const collator = new Intl.Collator(locale, { sensitivity: 'base' })
    return [...tenants].sort((a, b) => collator.compare(a.name, b.name))
  }, [tenants, locale])

  const term = fold(query.trim(), locale)
  const matches = React.useMemo(
    () => (term ? sorted.filter((tenant) => fold(tenant.name, locale).includes(term)) : sorted),
    [sorted, term, locale],
  )
  const activeIndex = Math.min(active, Math.max(matches.length - 1, 0))

  React.useEffect(() => setActive(0), [term])
  React.useEffect(() => {
    if (!autoFocus) return
    const frame = requestAnimationFrame(() => inputRef.current?.focus())
    return () => cancelAnimationFrame(frame)
  }, [autoFocus])
  React.useEffect(() => {
    document.getElementById(`${listId}-${activeIndex}`)?.scrollIntoView?.({ block: 'nearest' })
  }, [activeIndex, listId])

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (matches.length === 0) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive(Math.min(activeIndex + 1, matches.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive(Math.max(activeIndex - 1, 0))
    } else if (event.key === 'Home') {
      event.preventDefault()
      setActive(0)
    } else if (event.key === 'End') {
      event.preventDefault()
      setActive(matches.length - 1)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      onChoose(matches[activeIndex])
    }
  }

  const hasMatches = matches.length > 0

  return (
    <div className="flex min-h-0 flex-col gap-2">
      <Field id={`${listId}-search`} label={t('core.tenant.searchLabel')}>
        <Input
          ref={inputRef}
          type="search"
          role="combobox"
          aria-expanded={hasMatches}
          aria-controls={hasMatches ? listId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={hasMatches ? `${listId}-${activeIndex}` : undefined}
          autoComplete="off"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
        />
      </Field>
      <p className="text-xs text-fg-muted" role="status" aria-live="polite">
        {t('core.tenant.count', { count: matches.length })}
      </p>

      {hasMatches ? (
        <ul
          id={listId}
          role="listbox"
          aria-label={t('core.tenant.searchLabel')}
          className="max-h-80 overflow-y-auto rounded-md border border-line p-1"
        >
          {matches.map((tenant, index) => (
            <li
              key={tenant.id}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              onMouseMove={() => setActive(index)}
              onClick={() => onChoose(tenant)}
              className={cn(
                'flex min-h-11 cursor-pointer items-center gap-3 rounded-sm px-3 py-2 text-sm text-fg',
                index === activeIndex && 'bg-selected text-fg-strong',
              )}
            >
              <Building2 aria-hidden="true" className="size-4 shrink-0 text-fg-muted" />
              <span className="min-w-0 flex-1 break-words">{tenant.name}</span>
              {tenant.id === currentId ? (
                <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-success-text">
                  <Check aria-hidden="true" className="size-4" />
                  {t('core.tenant.current')}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <div className="rounded-md border border-line px-4 py-6 text-center">
          <p className="text-sm font-medium text-fg-strong">
            {t('core.tenant.noMatchTitle', { term: query.trim() })}
          </p>
          <p className="mt-1 text-sm text-fg-muted">{t('core.tenant.noMatchBody')}</p>
        </div>
      )}
    </div>
  )
}
